import React, { useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

import { createClientComment, getClientComments, getClientCommentTypes } from '../../../../api/clientComments';
import { ClientComment, CreateClientComment } from '../../../../types/clientComment';

const commentTypeColors = { 1: 'info', 2: 'error', 3: 'warning' } as const;
const getCommentTypeColor = (typeId: number) =>
  commentTypeColors[typeId as keyof typeof commentTypeColors] ?? 'default';

export const ClientComments: React.FC<{ agreementId: string }> = ({ agreementId }) => {
  const [text, setText] = useState('');
  const [type, setType] = useState('');
  const [saved, setSaved] = useState(false);
  const submitting = useRef(false);
  const queryClient = useQueryClient();
  const queryKey = ['clientComments', agreementId];
  const comments = useQuery({ queryKey, queryFn: () => getClientComments(agreementId) });
  const types = useQuery({ queryKey: ['clientCommentTypes'], queryFn: getClientCommentTypes, staleTime: Infinity });
  const mutation = useMutation({ mutationFn: (body: CreateClientComment) => createClientComment(agreementId, body) });
  const typeOptions = Object.entries(types.data ?? {});
  const selectedType = type || typeOptions.find(([id]) => id !== '0')?.[0] || typeOptions[0]?.[0] || '';
  const sortedComments = [...(comments.data ?? [])].sort(
    (a, b) => moment.utc(b.createdAt).valueOf() - moment.utc(a.createdAt).valueOf() || b.id - a.id,
  );

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!text.trim() || !selectedType || types.isError || submitting.current) return;

    submitting.current = true;
    setSaved(false);
    try {
      const comment = await mutation.mutateAsync({ text: text.trim(), type: Number(selectedType) });
      // Prevent an older in-flight GET from replacing the newly saved comment.
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<ClientComment[]>(queryKey, (previous = []) => [
        comment,
        ...previous.filter(({ id }) => id !== comment.id),
      ]);
      void queryClient.invalidateQueries({ queryKey });
      setText('');
      setSaved(true);
    } catch {
      // Keep the draft and selected type so the user can retry.
    } finally {
      submitting.current = false;
    }
  };

  return (
    <Paper component="section" aria-labelledby="client-comments-title" elevation={1} sx={{ p: 2, mt: 2 }}>
      <Typography id="client-comments-title" variant="h6" gutterBottom>
        Комментарии по клиенту
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Фиксируйте договорённости с филиалами и результаты разбора обращений по клиенту.
      </Typography>

      <Box component="form" onSubmit={submit}>
        <Stack spacing={2}>
          {types.isError && (
            <Alert severity="error" action={<Button onClick={() => types.refetch()}>Повторить</Button>}>
              Не удалось загрузить типы комментариев.
            </Alert>
          )}
          <TextField
            select
            label="Тип комментария"
            value={selectedType}
            onChange={(event) => setType(event.target.value)}
            disabled={types.isLoading || types.isError || mutation.isPending || !typeOptions.length}
            sx={{ width: { xs: '100%', sm: 280 } }}
          >
            {typeOptions.map(([id, name]) => (
              <MenuItem key={id} value={id}>
                <Chip label={name} color={getCommentTypeColor(Number(id))} size="small" variant="outlined" />
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Новый комментарий"
            placeholder="Опишите договорённость или результат разбора обращения"
            multiline
            minRows={3}
            maxRows={10}
            fullWidth
            value={text}
            disabled={mutation.isPending}
            onChange={(event) => {
              setText(event.target.value);
              setSaved(false);
              if (mutation.isError) mutation.reset();
            }}
          />
          {mutation.isError && (
            <Alert severity="error">
              Не удалось сохранить комментарий. {mutation.error instanceof Error ? mutation.error.message : ''}
            </Alert>
          )}
          {saved && <Alert severity="success">Комментарий добавлен</Alert>}
          <Button
            type="submit"
            variant="contained"
            disabled={!text.trim() || !selectedType || types.isError || types.isLoading || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{ alignSelf: 'flex-start' }}
          >
            {mutation.isPending ? 'Сохраняем…' : 'Добавить комментарий'}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ mt: 3 }} aria-busy={comments.isFetching}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          История комментариев{comments.data ? ` (${comments.data.length})` : ''}
        </Typography>
        {comments.isLoading && (
          <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={20} />
            <Typography variant="body2">Загружаем комментарии…</Typography>
          </Stack>
        )}
        {comments.isError && (
          <Alert severity="error" action={<Button onClick={() => comments.refetch()}>Повторить</Button>}>
            Не удалось загрузить историю комментариев.
          </Alert>
        )}
        {comments.isSuccess && sortedComments.length === 0 && (
          <Typography variant="body2" color="text.secondary" role="status">
            Комментариев пока нет. Добавьте первый комментарий.
          </Typography>
        )}
        <Stack component="ul" spacing={2} sx={{ p: 0, m: 0, listStyle: 'none', maxHeight: 480, overflowY: 'auto' }}>
          {sortedComments.map((comment) => (
            <Box component="li" key={comment.id} sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                  {comment.authorName}
                </Typography>
                <Chip
                  label={comment.typeName}
                  color={getCommentTypeColor(comment.typeId)}
                  size="small"
                  variant="outlined"
                />
                <Typography
                  component="time"
                  dateTime={moment.utc(comment.createdAt).toISOString()}
                  variant="caption"
                  color="text.secondary"
                >
                  {moment.utc(comment.createdAt).local().format('DD.MM.YYYY HH:mm')}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {comment.text}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};
